import { useQuery } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

export interface ConversationMessage {
  role: "user" | "assistant" | "tool";
  content: { type: "text"; text: string }[] | string;
  tool_calls?: {
    id: string;
    type: string;
    function: {
      name: string;
      arguments: string;
    };
  }[];
  tool_call_id?: string;
}

export interface ChatSession {
  id: string;
  workspace_id: number;
  created_at: string;
  updated_at: string;
  name: string;
  conversation_history: ConversationMessage[];
  created_by: number | null;
  isGeneratingName?: boolean;
}

export interface RecentVibeChatSessionsResponse {
  vibe_chat_sessions: ChatSession[];
  page: number;
  per_page: number;
  pages: number;
  total: number;
  has_next: boolean;
  has_prev: boolean;
  next_num: number | null;
  prev_num: number | null;
  success: boolean;
}

interface UseRecentVibeChatSessionsParams {
  page?: number;
  per_page?: number;
  enabled?: boolean;
  onSuccess?: (data: RecentVibeChatSessionsResponse) => void;
}

export const useRecentVibeChatSessions = ({
  page = 1,
  per_page = 8,
  enabled = true,
  onSuccess,
}: UseRecentVibeChatSessionsParams) => {
  const auth = useAuth();

  const defaultData: RecentVibeChatSessionsResponse = {
    vibe_chat_sessions: [],
    page: page,
    per_page: per_page,
    pages: 0,
    total: 0,
    has_next: false,
    has_prev: false,
    next_num: null,
    prev_num: null,
    success: false,
  };

  return useQuery<RecentVibeChatSessionsResponse>(
    ["recent-vibe-chat-sessions", page, per_page, auth?.userToken],
    async () => {
      if (!auth?.userToken) {
        return defaultData;
      }

      const url = new URL(ENDPOINTS.recent_vibe_chat_sessions);
      url.searchParams.set("page", page.toString());
      url.searchParams.set("per_page", per_page.toString());

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: jsonAuthHeaders(auth.userToken),
      });

      const data = await handleResponse(response);

      if (!data) {
        return defaultData;
      }

      const responseData = (data as { data?: RecentVibeChatSessionsResponse }).data || data;

      if (!responseData?.vibe_chat_sessions) {
        return defaultData;
      }

      return {
        vibe_chat_sessions: responseData.vibe_chat_sessions || [],
        page: responseData.page || page,
        per_page: responseData.per_page || per_page,
        pages: responseData.pages || 0,
        total: responseData.total || 0,
        has_next: responseData.has_next ?? false,
        has_prev: responseData.has_prev ?? false,
        next_num: responseData.next_num ?? null,
        prev_num: responseData.prev_num ?? null,
        success: responseData.success ?? true,
      };
    },
    {
      enabled: enabled && !!auth?.userToken,
      staleTime: 1000 * 30, // 30 seconds
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      placeholderData: defaultData,
      onSuccess,
    },
  );
};
